import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LockSimple,
  LockSimpleOpen,
  Clock,
  ShieldCheck,
  ShieldWarning,
} from "@phosphor-icons/react";

export default function SealIntegrityTimeline({ events }) {
  const analysis = useMemo(() => {
    if (!events || events.length === 0) return null;

    // Build timeline segments: consecutive runs of same light status
    const segments = [];
    let currentStatus = null;
    let segmentStart = null;
    let segmentStartIdx = 0;

    events.forEach((e, i) => {
      const light = (e.light || "").trim().toLowerCase();
      const status = light === "dark" || light === "" ? "sealed" : "breached";

      if (status !== currentStatus) {
        if (currentStatus !== null) {
          segments.push({
            status: currentStatus,
            startTime: segmentStart,
            endTime: e.timestamp,
            startIdx: segmentStartIdx,
            endIdx: i,
            count: i - segmentStartIdx,
          });
        }
        currentStatus = status;
        segmentStart = e.timestamp;
        segmentStartIdx = i;
      }
    });

    // Push last segment
    if (currentStatus !== null) {
      segments.push({
        status: currentStatus,
        startTime: segmentStart,
        endTime: events[events.length - 1].timestamp,
        startIdx: segmentStartIdx,
        endIdx: events.length,
        count: events.length - segmentStartIdx,
      });
    }

    // Calculate stats
    const totalEvents = events.length;
    const breachedSegments = segments.filter((s) => s.status === "breached");
    const sealedSegments = segments.filter((s) => s.status === "sealed");
    const breachedEvents = breachedSegments.reduce((s, seg) => s + seg.count, 0);
    const sealedEvents = sealedSegments.reduce((s, seg) => s + seg.count, 0);
    const integrityScore = Math.round((sealedEvents / Math.max(totalEvents, 1)) * 100);

    return {
      segments,
      breachedSegments,
      sealedSegments,
      breachedEvents,
      sealedEvents,
      totalEvents,
      integrityScore,
      isFullySealed: breachedEvents === 0,
    };
  }, [events]);

  if (!analysis) return null;

  const { segments, integrityScore, isFullySealed, breachedEvents, sealedEvents, totalEvents } = analysis;

  return (
    <Card
      data-testid="seal-integrity-timeline"
      className="bg-zinc-900 border-zinc-800 rounded-sm p-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
          Seal Integrity Timeline
        </h3>
        <Badge
          variant={isFullySealed ? "outline" : "destructive"}
          className={`text-xs font-mono ${isFullySealed ? "border-emerald-800 text-emerald-400" : ""}`}
        >
          {isFullySealed ? "Fully Sealed" : `${analysis.breachedSegments.length} Breach(es)`}
        </Badge>
      </div>

      {/* Integrity Score */}
      <div className="flex items-center gap-3 mb-4 p-3 rounded-sm bg-zinc-950/50">
        {isFullySealed ? (
          <ShieldCheck size={28} weight="duotone" className="text-emerald-400" />
        ) : (
          <ShieldWarning size={28} weight="duotone" className="text-amber-400" />
        )}
        <div>
          <p className="text-2xl font-mono font-semibold text-zinc-50">
            {integrityScore}%
          </p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
            Seal Integrity Score
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs font-mono text-zinc-400">
            {sealedEvents} sealed / {breachedEvents} breached
          </p>
          <p className="text-[10px] text-zinc-500">
            of {totalEvents} total readings
          </p>
        </div>
      </div>

      {/* Visual Timeline Bar */}
      <div className="mb-4">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
          Timeline
        </p>
        <div className="flex h-6 rounded-sm overflow-hidden border border-zinc-800">
          {segments.map((seg, i) => {
            const widthPct = (seg.count / totalEvents) * 100;
            return (
              <div
                key={i}
                className={`relative group transition-opacity duration-200 hover:opacity-80 ${
                  seg.status === "sealed"
                    ? "bg-emerald-500/30"
                    : "bg-amber-500/50 alert-pulse"
                }`}
                style={{ width: `${Math.max(widthPct, 0.5)}%` }}
                title={`${seg.status === "sealed" ? "Sealed" : "BREACHED"}: ${seg.startTime} to ${seg.endTime} (${seg.count} readings)`}
              >
                {widthPct > 8 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-mono text-zinc-300">
                    {seg.status === "sealed" ? (
                      <LockSimple size={10} weight="bold" />
                    ) : (
                      <LockSimpleOpen size={10} weight="bold" />
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[9px] font-mono text-zinc-600">
            {events[0]?.timestamp || "Start"}
          </span>
          <span className="text-[9px] font-mono text-zinc-600">
            {events[events.length - 1]?.timestamp || "End"}
          </span>
        </div>
      </div>

      {/* Segment Details */}
      <ScrollArea className="max-h-[180px]">
        <div className="space-y-1.5">
          {segments.map((seg, i) => (
            <div
              key={i}
              className={`flex items-center gap-2.5 p-2 rounded-sm text-xs ${
                seg.status === "sealed"
                  ? "bg-zinc-950/30"
                  : "bg-amber-500/5 border border-amber-900/30"
              }`}
            >
              {seg.status === "sealed" ? (
                <LockSimple
                  size={14}
                  weight="duotone"
                  className="text-emerald-400 shrink-0"
                />
              ) : (
                <LockSimpleOpen
                  size={14}
                  weight="duotone"
                  className="text-amber-400 shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-mono text-zinc-300 truncate">
                  {seg.status === "sealed" ? "Sealed" : "BREACH"}
                </p>
                <p className="text-[10px] text-zinc-500 truncate">
                  {seg.startTime} - {seg.endTime}
                </p>
              </div>
              <div className="flex items-center gap-1 text-zinc-500 shrink-0">
                <Clock size={11} weight="duotone" />
                <span className="text-[10px] font-mono">
                  {seg.count} readings
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
